import React, { useMemo } from 'react';
import { PresenceParticipant } from '../endpoints/presence/list_GET.schema';
import { CodeMirrorEditorView as MonacoEditor } from '../components/CodeMirrorEditor';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import styles from './CollaboratorCursorOverlay.module.css';

interface CollaboratorCursorOverlayProps {
  participant: PresenceParticipant;
  editorRef?: React.MutableRefObject<MonacoEditor | null>;
  scrollTrigger: { top: number; left: number }; // Used to trigger re-calc
}

export const CollaboratorCursorOverlay: React.FC<CollaboratorCursorOverlayProps> = ({
  participant,
  editorRef,
}) => {
  const { cursorLine, cursorColumn, color, displayName, avatarUrl } = participant;

  // Calculate position based on editor API
  const position = useMemo(() => {
    if (!editorRef?.current || !cursorLine || !cursorColumn) return null;

    try {
      // Monaco API: getScrolledVisiblePosition returns coordinates relative to the viewport
      // We might need to adjust based on how the container is positioned relative to the editor
      const pos = editorRef.current.getScrolledVisiblePosition({
        lineNumber: cursorLine,
        column: cursorColumn,
      });

      if (!pos) return null;

      return {
        top: pos.top,
        left: pos.left,
        height: pos.height,
      };
    } catch (e) {
      // Fallback or silence error if editor is not ready
      return null;
    }
  }, [editorRef, cursorLine, cursorColumn, editorRef?.current]); // Recalculate when these change

  if (!position) return null;

  // Create initials for avatar fallback
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div
      className={styles.cursorContainer}
      style={
        {
          '--cursor-color': color,
          transform: `translate(${position.left}px, ${position.top}px)`,
          height: `${position.height}px`,
        } as React.CSSProperties
      }
    >
      {/* The vertical cursor line */}
      <div className={styles.cursorLine} />

      {/* The name tag */}
      <div className={styles.nameTag}>
        <div className={styles.avatarWrapper}>
          <Avatar className={styles.avatar}>
            {avatarUrl && <AvatarImage src={avatarUrl} />}
            <AvatarFallback className={styles.avatarFallback} style={{ backgroundColor: color }}>
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
        <span className={styles.nameText}>{displayName}</span>
      </div>
    </div>
  );
};
